<template>
    <div>
        <v-row>
            <v-col cols="4" v-for="({ text, value, formatter }) in totalCards" :key="text">
                <v-card height="127">
                    <v-card-subtitle class="">{{ text }}</v-card-subtitle>
                    <v-divider />
                    <v-card-text>
                        <div v-if="loading" style="height: 100%" class="d-flex justify-center align-center">
                            <v-icon size="30px" class="fa-spin" >
                                fa-spinner
                            </v-icon>                    
                        </div>
                        <div v-else>
                            <div class="display-1 text--primary">{{ formatter(value(totals)) }}</div>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
            <v-col cols="4" v-for="({ text, value, formatter }) in totalDeviationCards" :key="text">
                <v-card height="127">
                    <v-card-subtitle class="">{{ text }}</v-card-subtitle>
                    <v-divider />
                    <v-card-text>
                        <div v-if="loading" style="height: 100%" class="d-flex justify-center align-center">
                            <v-icon size="30px" class="fa-spin" >
                                fa-spinner
                            </v-icon>                    
                        </div>
                        <div v-else>
                            <div class="display-1 text--primary">{{ formatter(value(totals)) }}</div>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
        <v-row>
            <v-col>
                <loader v-if="loading" />
                <v-card v-else>
                    <v-card-text>
                        <v-subheader>
                            <v-row>
                                <v-col v-for="header in headers" :key="header.text" @click="setSort(header.field)">
                                    {{ header.text }}
                                    <v-icon v-if="sortField === header.field && sortOrder">fa-angle-down</v-icon>
                                    <v-icon v-else-if="sortField === header.field">fa-angle-up</v-icon>
                                </v-col>
                            </v-row>
                        </v-subheader>
                        <v-divider />
                        <v-virtual-scroll height="70vh" item-height="64" :items="sortedAgents" bench="1000" class="striped-scroll">
                            <template v-slot:default="{ item: agent }">
                                <v-list-item>
                                    <v-list-item-content>
                                        <v-row align="baseline">
                                            <v-col v-for="header in headers" :key="header.text">
                                                <span v-if="header.deviation && agent[header.field] / agent.ajour >= 0.05" class="red--text">{{ header.formatter(agent[header.field]) }}</span>
                                                <span v-else-if="header.deviation && agent[header.field] / agent.ajour >= 0.02" class="orange--text">{{ header.formatter(agent[header.field]) }}</span>
                                                <span v-else>{{ header.formatter(agent[header.field]) }}</span>
                                            </v-col>
                                        </v-row>
                                    </v-list-item-content>
                                </v-list-item>
                            </template>
                        </v-virtual-scroll>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </div>
</template>

<script>
export default {
    data () {
        return {
            loading: true,
            agents: null,
            sortField: 'grossAjour',
            sortOrder: true,
            headers: [
                {
                    text: 'Navn',
                    field: 'name',
                    formatter: v => v
                },
                {
                    text: 'AS',
                    field: 'asTotal',
                    formatter: this.$options.filters.separator
                },
                {
                    text: 'Ajour',
                    field: 'ajour',
                    formatter: this.$options.filters.separator
                },
                {
                    text: 'Salgsrapport',
                    field: 'gross',
                    formatter: this.$options.filters.separator
                },
                {
                    text: 'AS/Ajour',
                    field: 'asAjour',
                    formatter: this.$options.filters.separator,
                    deviation: true
                },
                {
                    text: 'AS/Salgsrapport',
                    field: 'asGross',
                    formatter: this.$options.filters.separator,
                    deviation: true
                },
                {
                    text: 'Salgsrapport/Ajour',
                    field: 'grossAjour',
                    formatter: this.$options.filters.separator,
                    deviation: true
                }
            ],
            totalCards: [
                {
                    text: 'Agenturstats',
                    value: (stats) => stats.asTotal,
                    formatter: this.$options.filters.separator
                },
                {
                    text: 'Ajour',
                    value: (stats) => stats.ajour,
                    formatter: this.$options.filters.separator
                },
                {
                    text: 'Salgsrapport',
                    value: (stats) => stats.gross,
                    formatter: this.$options.filters.separator
                }
            ],
            totalDeviationCards: [
                {
                    text: 'Agenturstats/Ajour',
                    value: (stats) => Math.abs(stats.asTotal - stats.ajour),
                    formatter: this.$options.filters.separator
                },
                {
                    text: 'Agenturstats/Salgsrapport',
                    value: (stats) => Math.abs(stats.asTotal - stats.gross),
                    formatter: this.$options.filters.separator
                },
                {
                    text: 'Salgsrapport/Ajour',
                    value: (stats) => Math.abs(stats.gross - stats.ajour),
                    formatter: this.$options.filters.separator
                }
            ]
        }
    },
    computed: {
        filteredAgents () {
            if (!this.agents) return []
            return this.agents.filter(agent => {
                return agent.name.toLowerCase().includes(this.$store.state.quality.searchString.toLowerCase())
            })
        },
        sortedAgents () {
            const order = this.sortOrder ? -1 : 1
            return [...this.filteredAgents].sort((a, b) => a[this.sortField] > b[this.sortField] ? order : -order)
        },
        department () {
            return this.$store.getters['quality/department']
        },
        year () {
            return this.$store.state.quality.year
        },
        startWeek () {
            return this.$store.state.quality.startWeek
        },
        endWeek () {
            return this.$store.state.quality.endWeek
        },
        ytd () {
            return this.$store.state.quality.ytd
        },
        inactive () {
            return this.$store.state.quality.inactive
        },
        team () {
            return this.$store.state.quality.team
        },
        totals () {
            return this.$reduceNested(this.agents, ['asTotal', 'ajour', 'gross'])
        },
        searchString () {
            return this.$store.state.quality.searchString
        }
    },
    methods: {
		setSort(field) {
			if (this.sortField === field) this.sortOrder = !this.sortOrder;
			else {
                this.sortField = field
                this.sortOrder = true
            }
		},
        async getData () {
            this.loading = true
            const response = await this.$http().get('quality/status/deviations', {
                params: {
                    year: this.$store.state.quality.year,
                    startWeek: this.$store.state.quality.startWeek,
                    endWeek: this.$store.state.quality.endWeek,
                    team: this.team,
                    department: this.department,
                    inactive: this.$store.state.quality.inactive
                }
            })
            this.agents = response.data
            this.loading = false
        }
    },
    watch: {
        selectedAgent: function () {
            this.getComplaints(this.selectedAgent)
        },
        department: function () {
            this.getData()
        },
        year: function () {
            this.getData()
        },
        startWeek: function () {
            this.getData()
        },
        endWeek: function () {
            this.getData()
        },
        ytd: function () {
            this.getData()
        },
        team: function () {
            this.getData()
        },
        inactive: function () {
            this.getData()
        }
    },
    created () {
        this.$store.state.quality.searchString = ''
        this.getData()
    }
}
</script>